import {
  Box,
  Breadcrumbs,
  Divider,
  makeStyles,
  Paper,
  Theme,
  Typography,
  useTheme,
} from "@material-ui/core";
import {
  Datagrid,
  Error,
  FunctionField,
  Link,
  ListBase,
  Loading,
  Pagination,
  ReactAdminComponentProps,
  Record,
  ResourceContextProvider,
  TextField,
  TitleForRecord,
  useGetOne,
  useLocale,
  useRefresh,
  useTranslate,
} from "react-admin";
import { RouteComponentProps } from "react-router-dom";
import resourceNames from "../../../../common/data/resourceNames";
import { AppState, CustomResourceRouteParams } from "../../../../common/types";
import { DvirDefectDto, VehicleDvirHistoryEntryDto } from "../types";
import IndeterminateCheckBoxIcon from "@material-ui/icons/IndeterminateCheckBox";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import "./VehicleDvirView.css";
import { format, parseISO } from "date-fns";
import {
  dateFnsLocaleMap,
  ProLogsLocale,
} from "../../../../common/util/localization";
import { enUS } from "date-fns/locale";
import { DefectField } from "./DefectField";
import { ActionsField } from "../../../common/data-grid-fields/ActionsField";
import { ResolveDefectMenuItem } from "./ResolveDefectMenuItem";
import "../../../common/styles/list-style.css";
import { useSelector } from "react-redux";
import { ImagesField } from "../../../common/data-grid-fields/ImagesField";
import { useAxiosInstance } from "../../../../common/hooks/useAxiosInstance";
import { DvirHistoryApiFactory } from "../../../../common/data/service-proxies";
import permissionNames from "../../../../common/auth/access-control/permissionNames";
import { useActionPermissions } from "../../../../common/auth/useActionPermissions";

interface Props extends RouteComponentProps<CustomResourceRouteParams> {}

interface BreadcrumbProps {
  data: VehicleDvirHistoryEntryDto;
  basePath: string;
}

const useStyles = makeStyles(
  {
    root: {},
    main: {
      display: "flex",
    },
    noActions: {
      marginTop: "1em",
    },
    card: {
      flex: "1 1 auto",
    },
  },
  { name: "RaShow" }
);

const Breadcrumb = ({ data, basePath }: BreadcrumbProps) => {
  const translate = useTranslate();
  const locale = useLocale() as ProLogsLocale;
  const dateFnsLocale = !!dateFnsLocaleMap[locale]
    ? dateFnsLocaleMap[locale]
    : enUS;

  return (
    <Breadcrumbs aria-label="breadcrumb">
      <Link to={`/${resourceNames.ASSETS_DVIR_HISTORY}`}>
        <Typography variant="h6">
          {translate("resources.assets-dvir-vehicle-history.name")}
        </Typography>
      </Link>
      <Link to={`${basePath}`}>
        <Typography variant="h6">{data.vehicleName}</Typography>
      </Link>
      <Typography variant="h6">
        {translate(`resources.assets-dvir-vehicle-history.types.${data.type}`)}{" "}
        {format(parseISO(data.date!), "PPP", {
          locale: dateFnsLocale,
        })}
      </Typography>
    </Breadcrumbs>
  );
};

export const VehicleDvirView = (props: Props & ReactAdminComponentProps) => {
  const translate = useTranslate();
  const classes = useStyles();
  const theme: Theme = useTheme();
  const dvirId = props.match?.params.dvirId;
  const { loading, error, data } = useGetOne<
    VehicleDvirHistoryEntryDto & Record
  >(resourceNames.ASSETS_DVIR_VEHICLE_HISTORY, dvirId ?? 0);
  const locale = useLocale() as ProLogsLocale;
  const dateFnsLocale = !!dateFnsLocaleMap[locale]
    ? dateFnsLocaleMap[locale]
    : enUS;
  const themeName = useSelector((x: AppState) => x.theme);
  const refresh = useRefresh();
  const axios = useAxiosInstance();
  const permissions = useActionPermissions();

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <Error error={error} />;
  }

  if (!data) {
    return null;
  }

  return (
    <div id="vehicle-dvir-view" className="content-container">
      <TitleForRecord
        title={<Breadcrumb data={data} basePath={props.basePath!} />}
        record={data}
      />
      <Paper id="vehicle-dvir-view-container" className={classes.card}>
        <div id="vehicle-dvir-header-container">
          <div className="header-row">
            <div className="vehicle-dvir-header-info">
              <strong>
                {translate(
                  "resources.assets-dvir-vehicle-history.labels.driverFullName"
                )}
              </strong>
              <div>{data.driverName}</div>
            </div>
            <div className="vehicle-dvir-header-info">
              <strong>
                {translate(
                  "resources.assets-dvir-vehicle-history.labels.vehicleName"
                )}
              </strong>
              <div>{data.vehicleName}</div>
            </div>
            <div className="vehicle-dvir-header-info">
              <strong>
                {translate(
                  "resources.assets-dvir-vehicle-history.labels.odometer"
                )}
              </strong>
              <div>
                {data.vehicleOdometer} {data.vehicleOdometerUnits}
              </div>
            </div>
            <div className="vehicle-dvir-header-info">
              <strong>
                {translate(
                  "resources.assets-dvir-vehicle-history.labels.companyName"
                )}
              </strong>
              <div>{data.companyName}</div>
            </div>
          </div>
          <div className="header-row">
            <div className="vehicle-dvir-header-info">
              <strong>
                {translate(
                  "resources.assets-dvir-vehicle-history.labels.dateTime"
                )}
              </strong>
              <div>
                {format(parseISO(data.date!), "PPPp", {
                  locale: dateFnsLocale,
                })}
              </div>
            </div>
            <div className="vehicle-dvir-header-info">
              <strong>
                {translate(
                  "resources.assets-dvir-vehicle-history.labels.trailerName"
                )}
              </strong>
              <div>{data.trailerName}</div>
            </div>
            <div className="vehicle-dvir-header-info">
              <strong>
                {translate(
                  "resources.assets-dvir-vehicle-history.labels.location"
                )}
              </strong>
              <div>{data.location}</div>
            </div>
            <div className="vehicle-dvir-header-info">
              <strong>
                {translate("resources.assets-dvir-vehicle-history.labels.type")}
              </strong>
              <div>
                {translate(
                  `resources.assets-dvir-vehicle-history.types.${data.type}`
                )}
              </div>
            </div>
          </div>
        </div>
        <Divider />
        <div className="vehicle-dvir-defects-list">
          <Typography variant="h6">
            {translate("resources.assets-dvir-vehicle-history.labels.defects")}
          </Typography>
          <ResourceContextProvider
            value={resourceNames.ASSETS_DVIR_VEHICLE_DEFECTS}>
            <div className="vehicle-dvir-defects-list-inner">
              <ListBase filter={{ dvirId: data.id }} perPage={5} {...props}>
                <Datagrid>
                  <DefectField label="resources.assets-dvir-vehicle-history.labels.defectDescription" />
                  <FunctionField<DvirDefectDto & Record>
                    source="severity"
                    label="resources.assets-dvir-vehicle-history.labels.severity"
                    headerClassName="centered-content"
                    render={(record) =>
                      record?.severity && (
                        <div className="centered-content">
                          <Typography variant="body2">
                            {translate(
                              `resources.admin-dvir-forms.severities.${record.severity}`
                            )}
                          </Typography>
                        </div>
                      )
                    }
                  />
                  <ImagesField<DvirDefectDto & Record>
                    label="resources.assets-dvir-vehicle-history.labels.photos"
                    headerClassName="centered-content"
                    className="centered-content"
                    getRestCount={(record) => record?.otherPhotosCount ?? 0}
                    getImages={(record) => {
                      if (!axios) {
                        return [];
                      }

                      if (!record?.id) {
                        return [];
                      }

                      return DvirHistoryApiFactory(
                        undefined,
                        axios.defaults.baseURL,
                        axios
                      )
                        .apiAppDvirvehicledefectsGetPhotosPost(
                          Number(record.id)
                        )
                        .then((result) => result.data);
                    }}
                    getThumbnail={(record) =>
                      record?.thumbnailPhoto ?? "https://via.placeholder.com/80"
                    }
                    isDarkTheme={themeName === "dark"}
                  />
                  <FunctionField<DvirDefectDto & Record>
                    source="isResolved"
                    headerClassName="centered-content"
                    label="resources.assets-dvir-vehicle-history.labels.isResolved"
                    render={(record) => (
                      <Box className="centered-content">
                        {record?.isResolved ? (
                          <CheckBoxIcon
                            style={{
                              color: theme.palette.success.main,
                            }}
                          />
                        ) : (
                          <IndeterminateCheckBoxIcon
                            style={{
                              color: theme.palette.error.main,
                            }}
                          />
                        )}
                      </Box>
                    )}
                  />
                  <TextField
                    source="comment"
                    label="resources.assets-dvir-vehicle-history.labels.comment"
                  />
                  <ActionsField editable={() => false}>
                    {permissions[
                      permissionNames.assets.dvirHistory.manageDefects
                    ] ? (
                      <ResolveDefectMenuItem onSuccess={() => refresh()} />
                    ) : null}
                  </ActionsField>
                </Datagrid>
                <Pagination />
              </ListBase>
            </div>
          </ResourceContextProvider>
        </div>
      </Paper>
    </div>
  );
};
